import { isSdpEnabled } from '@99designs/feature-service-details';
import {
    DS_BRIEF_CREATE_URL,
    __,
    __product,
    __url,
    asNinetyNineAbsUrl,
    getCountryCode,
} from '@99designs/i18n';
import { FeaturedProductsType } from './graph/getProductsByKey';
import careBusinessCard from './images/product-tiles/business-card.png';
import careFlyer from './images/product-tiles/flyer.png';
import ninetyNineLogoAndBrand from './images/product-tiles/logo-brand.png';
import ninetyNineLogo from './images/product-tiles/logo.png';
import type { TrackingData } from '@99designs/tracking';
import type { DesignServicesProductNames, ProductUIConfig } from './types';

export function getFeaturedCareProducts(): Partial<FeaturedProductsType> {
    const FeaturedCareProducts = {
        'business-card-design': {
            title: __('Business Card Design'),
            turnaroundTime: 24,
        },
        'flyer-design': {
            title: __('Flyer Design'),
            turnaroundTime: 24,
        },
    };
    return FeaturedCareProducts;
}

export function useUIConfigForProduct(
    productKey: DesignServicesProductNames,
    locale: string,
    isBifrostEnabled?: boolean
): ProductUIConfig {
    const ctaCopy = {
        nineNine: __('Get a design on 99designs by Vista'),
        vista: __('Get a design'),
    };

    // NOTE: If the mpvID is undefined for a locale, then we can infer that the product is unavailable
    const productConfig = {
        'business-card-design': {
            isAvailableInLocale: !!__product('mpv-id-business-card'),
            ctaText: ctaCopy.vista,
            ctaLink: `/design/get-started?mpvId=${__product(
                'mpv-id-business-card'
            )}&locale=${locale}`,
            image: {
                url: careBusinessCard,
                designer: {
                    userId: 'vistaprint',
                    displayName: __('VistaPrint in-house design'),
                },
                isDarkImage: false,
                accessibleText: __('Business Card Design Example'),
            },
        },
        'logo-essential': {
            isAvailableInLocale: true,
            ctaText: ctaCopy.nineNine,
            ctaLink: isBifrostEnabled
                ? __url('/design/logo-essential')
                : `${__url('/logo-design')}`,
            image: {
                url: ninetyNineLogo,
                isDarkImage: true,
                designer: {
                    userId: '4356353',
                    displayName: 'artantoni work',
                },
                accessibleText: __('Logo Design - {{designer}}', {
                    designer: ' Mandate Coffee',
                }),
            },
        },
        'flyer-design': {
            isAvailableInLocale: !!__product('mpv-id-flyer'),
            ctaText: ctaCopy.vista,
            ctaLink: `/design/get-started?mpvId=${__product('mpv-id-flyer')}&locale=${locale}`,
            image: {
                url: careFlyer,
                designer: {
                    userId: 'vistaprint',
                    displayName: __('VistaPrint in-house design'),
                },
                isDarkImage: false,
                accessibleText: __('Flyer Design Example'),
            },
        },
        'brand-identity-pack': {
            isAvailableInLocale: true,
            ctaText: ctaCopy.nineNine,
            ctaLink: asNinetyNineAbsUrl(`/brand-identity-pack/details`),
            image: {
                url: ninetyNineLogoAndBrand,
                isDarkImage: true,
                designer: {
                    userId: '2829526',
                    displayName: 'leonelbalza',
                },
                accessibleText: __('Logo & brand identity Example'),
            },
        },
    };
    return productConfig[productKey];
}

export function useTrackingForProduct(
    productKey: DesignServicesProductNames,
    locale: string,
    anonymousId: string,
    isBifrostEnabled = false
): TrackingData {
    const countryCode = getCountryCode(locale);
    const crossPlatformTracking = `?utm_campaign=Design-Services-${countryCode}&utm_content=Product-Tile&vistaprint_aid=${anonymousId}`;

    const careBusinessCardSdpEventDetail =
        '/design;/design/business-card-design;Design Services Tiles;BusinessCardDesign-CTA';
    const careBusinessCardEventDetail = `/design;${DS_BRIEF_CREATE_URL}/business-card-design?mpvId=${__product(
        'mpv-id-business-card'
    )};Design Services Tiles;BusinessCardDesign-CTA`;

    const productTracking = {
        'business-card-design': {
            label: 'Content:Design Services Tiles',
            eventDetail: isSdpEnabled('business-card-design', locale)
                ? careBusinessCardSdpEventDetail
                : careBusinessCardEventDetail,
            navigationDetail: 'BusinessCardDesign-CTA:1',
        },
        'logo-essential': {
            label: 'Content:Design Services Tiles',
            eventDetail: isBifrostEnabled
                ? `${__url(
                      '/logo-design'
                  )}${crossPlatformTracking};Design Services Tiles;LogoDesign-CTA`
                : '/design/logo-essential',
            navigationDetail: 'LogoDesign-CTA:2',
            crossPlatformTracking: isBifrostEnabled ? undefined : crossPlatformTracking,
        },
        'flyer-design': {
            label: 'Content:Design Services Tiles',
            eventDetail: `/design;${DS_BRIEF_CREATE_URL}/care-design?mpvId=${__product(
                'mpv-id-flyer'
            )};Design Services Tiles;FlyerDesign-CTA`,
            navigationDetail: 'FlyerDesign-CTA:3',
        },
        'brand-identity-pack': {
            label: 'Content:Design Services Tiles',
            eventDetail: `/design;${asNinetyNineAbsUrl(
                `/brand-identity-pack${crossPlatformTracking}`
            )};Design Services Tiles;LogoBrandIdentityDesign-CTA`,
            navigationDetail: 'LogoBrandIdentityDesign-CTA:4',
            crossPlatformTracking,
        },
    };
    return productTracking[productKey];
}
